import React, { useState, useEffect, useRef } from "react";
import type { Recipe } from "../../types/Recipe";
import { useFontenehus } from "../../context/FontenehusContext";
import { useSuggestionApi } from "../../api/SuggestionApi";
import { useUser } from "../../context/UserContext";
import { handleApiError } from "../../utils/handleApiError";
import { toast } from "react-toastify";
import type { Filters } from "../FilterModal";
import FilterModal from "../FilterModal";
import RecipeDetail from "../../pages/RecipeDetailPage";
import { useModalContext } from "../../context/ModalContext";
import { createPortal } from "react-dom";
import { useCommonFunctions } from "../../utils/common";
import { useRefresh } from "../../context/RefreshContext ";
import { useConfirm } from "../../context/ConfirmContext ";

interface SuggestedLunch {
  id: number;
  recipeId: number;
  suggestedById?: number;
  fontenehusId?: number;
  date: string;
  recipe?: Recipe;
  recipeImageUrl?: string;
  suggestedByUsername?: string;
  anonymous: boolean;
  recipeTitle?: string;
  likeCount?: number;
  isLikedByCurrentUser?: boolean;
  suggestedByDisplayname?: string;
}

interface SuggestedLunchesProps {
  allRecipes: Recipe[];
  currentUser?: string;
  initialSuggestions?: SuggestedLunch[];
  date: string;
  onAddRecipeFromSuggestion?: (date: string, recipeId: number) => void;
  recipeCategories: any;
  allergensList: any;
  ingredientsList: any;
  onSearchChange?: (value: string) => void;
  setModalRecipeId: (id: number | undefined) => void;
}

const SuggestedLunches: React.FC<SuggestedLunchesProps> = ({
  allRecipes,
  initialSuggestions = [],
  onAddRecipeFromSuggestion,
  onSearchChange,
  date,
  recipeCategories,
  allergensList,
  ingredientsList,
  setModalRecipeId,
}) => {
  const { user } = useUser();
  const { selectedFontenehus, fontenehusList } = useFontenehus();
  const { setIsAnyModalOpen } = useModalContext();
  const {
    getSuggestionByDate,
    createSuggestion,
    deleteSuggestion,
    toggleLike,
  } = useSuggestionApi();
  const { refreshKey } = useRefresh();
  const [selectedId, setSelectedId] = useState<number | "">("");
  const [suggestedList, setSuggestedList] =
    useState<SuggestedLunch[]>(initialSuggestions);
  const [anonymous, setAnonymous] = useState(
    user?.anonymousSuggestions || false,
  );

  const [showModal, setShowModal] = useState(false);
  const [showModalRecipe, setShowModalRecipe] = useState<number | undefined>(
    undefined,
  );
  const [search, setSearch] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const confirm = useConfirm();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [localFilters, setFilters] = useState<Filters>({
    categoryIds: [],
    fontenehusIds: [],
    allergenIds: [],
    ingredientIds: [],
  });

  const [floatingSuggestions, setFloatingSuggestions] = useState<
    SuggestedLunch[]
  >([]);
  const [floatingLoading, setFloatingLoading] = useState(false);
  const [showFloatingSuggestions, setShowFloatingSuggestions] = useState(false);

  useEffect(() => {
    setIsAnyModalOpen(showModal || !!showModalRecipe);
  }, [showModal, showModalRecipe, setIsAnyModalOpen]);

  const canAddRecipeToDailyMenu = user?.roles?.some((r) =>
    ["MenuResponsible"].includes(r),
  );
  const { canAddSuggestion, canLike } = useCommonFunctions(
    user,
    selectedFontenehus,
  );

  const canRemoveSuggestion = (suggestedById?: number) =>
    user?.roles?.includes("MenuResponsible") || user?.id === suggestedById;

  const availableRecipes = allRecipes.filter(
    (r) => !suggestedList.some((s) => s.recipeId === r.id),
  );

  const filteredRecipes = availableRecipes.filter((recipe) => {
    const matchesSearch = recipe.title
      .toLowerCase()
      .includes(search.toLowerCase());
    const matchesCategory =
      localFilters.categoryIds.length === 0 ||
      (recipe.categories ?? []).some((rc) =>
        localFilters.categoryIds.includes(rc.id),
      );
    const matchesFontenehus =
      localFilters.fontenehusIds.length === 0 ||
      localFilters.fontenehusIds.includes(recipe.fontenehusId);
    const matchesAllergens =
      localFilters.allergenIds.length === 0 ||
      !(recipe.allergens ?? []).some((a) =>
        localFilters.allergenIds.includes(a.id),
      );
    const matchesIngredients =
      localFilters.ingredientIds.length === 0 ||
      (recipe.ingredients ?? []).some((ri) =>
        localFilters.ingredientIds.includes(ri.id),
      );
    return (
      matchesSearch &&
      matchesCategory &&
      matchesFontenehus &&
      matchesAllergens &&
      matchesIngredients
    );
  });

  const selectedRecipe = availableRecipes.find((r) => r.id === selectedId);

  // Autofocus search input
  useEffect(() => {
    if (showModal && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [showModal]);

  useEffect(() => {
    if (!date) return;
    getSuggestionByDate(date)
      .then((res) => setSuggestedList(res.data.data || []))
      .catch((err) =>
        handleApiError(err, "Kunne ikke hente forslag for valgt dato."),
      );
  }, [date, selectedFontenehus, refreshKey]);

  const handleAddSuggestion = async () => {
    if (!selectedId) return;
    const recipe = allRecipes.find((r) => r.id === Number(selectedId));
    if (!recipe) return;

    const newSuggestion: SuggestedLunch = {
      id: 0,
      recipeId: recipe.id,
      date,
      anonymous,
      fontenehusId: selectedFontenehus?.id,
    };

    try {
      const res = await createSuggestion(newSuggestion);
      setSuggestedList((prev) => [...prev, res.data.data]);
      setSelectedId("");
      setShowModal(false);
      setSearch("");
      toast.success(`Forslag med ${recipe.title} er opprettet.`);
    } catch (err: any) {
      handleApiError(err, "Feil ved oppretting av forslag.");
    }
  };

  const handleLike = async (suggestionId: number, isFloating: boolean) => {
    try {
      const res = await toggleLike(suggestionId);
      const data = res.data.data; // { suggestionId, likeCount, isLikedByCurrentUser }
      if (!isFloating)
        setSuggestedList((prev) =>
          prev.map((s) =>
            s.id === suggestionId
              ? {
                  ...s,
                  likeCount: data.likeCount,
                  isLikedByCurrentUser: data.isLikedByCurrentUser,
                }
              : s,
          ),
        );
      else
        setFloatingSuggestions((prev) =>
          prev.map((s) =>
            s.id === suggestionId
              ? {
                  ...s,
                  likeCount: data.likeCount,
                  isLikedByCurrentUser: data.isLikedByCurrentUser,
                }
              : s,
          ),
        );
    } catch (err) {
      handleApiError(err, "Feil ved liking av forslag");
    }
  };

  const handleRemove = async (id: number, dateless: boolean) => {
    // Ask for confirmation first
    const ok = await confirm({
      title: "Slett forslag",
      message: "Er du sikker på at du vil slette dette forslaget?",
      confirmText: "Slett",
    });

    if (!ok) return;

    try {
      await deleteSuggestion(id);

      if (dateless) {
        setFloatingSuggestions((prev) => prev.filter((s) => s.id !== id));
      } else {
        setSuggestedList((prev) => prev.filter((s) => s.id !== id));
      }

      toast.success("Forslag fjernet.");
    } catch (err: any) {
      handleApiError(err, "Uventet feil ved fjerning av forslag.");
    }
  };

  const handleSelectRecipe = (recipeId: number) => {
    setSelectedId(recipeId);
    setShowModal(false);
    setSearch("");
  };

  const handleClearSelection = () => {
    setSelectedId("");
    setShowModal(false);
    setSearch("");
  };

  const loadFloatingSuggestions = async () => {
    try {
      setFloatingLoading(true);
      const res = await getSuggestionByDate(undefined);
      setFloatingSuggestions(res.data.data || []);
    } catch (err) {
      handleApiError(err, "Kunne ikke hente udaterte forslag.");
    } finally {
      setFloatingLoading(false);
    }
  };

  useEffect(() => {
    if (refreshKey > 0) {
      const loadFloatingSuggestions = async () => {
        try {
          setFloatingLoading(true);
          const res = await getSuggestionByDate(undefined);
          setFloatingSuggestions(res.data.data || []);
        } catch (err) {
          handleApiError(err, "Kunne ikke hente udaterte forslag.");
        } finally {
          setFloatingLoading(false);
        }
      };

      loadFloatingSuggestions();
    }
  }, [refreshKey]);

  return (
    <div className="mt-8 bg-orange-50 rounded-xl shadow-sm p-5">
      {/* Suggested lunches */}
      <h3 className="text-lg font-semibold text-orangeBrand mb-4">
        🍽️ Foreslåtte lunsjer
      </h3>
      {suggestedList.length === 0 ? (
        <p className="text-gray-600 italic">Ingen foreslåtte lunsjer enda.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {suggestedList.map((item) => (
            <div
              key={item.id}
              className="bg-orange-50 rounded-lg shadow-sm p-3 flex flex-col items-center"
            >
              <div
                onClick={() => setModalRecipeId(item.recipeId)}
                className="bg-orange-50 hover:bg-orange-100 p-3 rounded-lg shadow-sm text-left cursor-pointer"
              >
                <img
                  src={
                    item.recipeImageUrl || "/assets/no-image-placeholder.webp"
                  }
                  alt={item.recipeTitle}
                  className="w-28 h-28 object-cover rounded-lg mb-2"
                />
                <p className="font-medium text-orangeBrand text-center">
                  {item.recipeTitle}
                </p>
                <p className="text-sm text-gray-600 mt-1 text-center">
                  👤 Foreslått av{" "}
                  <strong>
                    {(item.suggestedByDisplayname ||
                      item.suggestedByUsername) ??
                      "Anonym"}
                  </strong>
                </p>
              </div>

              {/* Like Section */}
              <div className="flex items-center justify-between w-full mt-2 px-2">
                <button
                  onClick={() => canLike && handleLike(item.id, false)}
                  disabled={!canLike}
                  className={`flex items-center gap-1 px-3 py-1 rounded-md text-sm transition-colors ${
                    item.isLikedByCurrentUser
                      ? "bg-blue-500 text-white hover:bg-blue-600"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  } ${!canLike ? "cursor-not-allowed opacity-60" : ""}`}
                >
                  <span>👍</span>
                  <span>
                    {canLike
                      ? item.isLikedByCurrentUser
                        ? "Likt"
                        : "Lik"
                      : "Liker"}
                  </span>
                </button>

                <div className="text-sm text-gray-600">
                  {item.likeCount! > 0 && (
                    <span>
                      {item.likeCount} liker{item.likeCount !== 1 ? "" : ""}
                    </span>
                  )}
                </div>
              </div>

              {canAddRecipeToDailyMenu && (
                <button
                  onClick={() =>
                    onAddRecipeFromSuggestion?.(date, item.recipeId)
                  }
                  className="mt-2 bg-green-500 hover:bg-green-600 text-white text-sm px-3 py-1 rounded-md flex items-center gap-1"
                >
                  Legg til i menyen
                </button>
              )}
              {canRemoveSuggestion(item.suggestedById) && (
                <button
                  onClick={() => handleRemove(item.id, false)}
                  className="mt-2 bg-red-500 hover:bg-red-600 text-white text-sm px-3 py-1 rounded-md"
                >
                  ❌ Fjern
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add new suggestion */}
      {canAddSuggestion && (
        <div className="flex flex-col items-center justify-center mt-6 w-full">
          <div className="flex items-center gap-3 w-full max-w-[800px]">
            <div className="relative flex-1 w-full">
              <div
                className="w-full h-full border border-gray-300 rounded-md px-3 py-2 cursor-pointer bg-white text-left flex items-center justify-between"
                onClick={() => setShowModal(true)}
              >
                <span
                  className={selectedRecipe ? "text-gray-800" : "text-gray-500"}
                >
                  {selectedRecipe ? selectedRecipe.title : "Velg forslag"}
                </span>
              </div>
            </div>
            <button
              disabled={!selectedId}
              onClick={handleAddSuggestion}
              className={`px-4 py-2 rounded-md font-semibold whitespace-nowrap transition h-[calc(2.5rem+8px)] ${selectedId ? "bg-orangeBrand hover:bg-orangeHover text-white" : "bg-gray-300 text-gray-500 cursor-not-allowed"}`}
            >
              ➕ Legg til forslag
            </button>
          </div>
          <div className="flex items-center justify-center gap-2 mt-3">
            <input
              type="checkbox"
              id="anonymous"
              checked={anonymous}
              onChange={(e) => setAnonymous(e.target.checked)}
              className="w-4 h-4 accent-orangeBrand cursor-pointer"
            />
            <label
              htmlFor="anonymous"
              className="text-sm text-gray-700 cursor-pointer select-none"
            >
              Jeg vil foreslå anonymt
            </label>
          </div>
        </div>
      )}

      {/* Search Modal Portal */}
      {showModal &&
        createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-auto">
            <div className="bg-white rounded-lg w-full max-w-md flex flex-col shadow-lg">
              {/* Header */}
              <div className="p-4 border-b border-gray-200 flex-shrink-0">
                <div className="flex justify-between items-center mb-3">
                  <h2 className="text-lg font-semibold">
                    Velg oppskrift å foreslå
                  </h2>
                  <button
                    onClick={handleClearSelection}
                    className="text-gray-400 hover:text-gray-600 text-2xl"
                  >
                    ×
                  </button>
                </div>
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Skriv for å søke..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    onSearchChange?.(e.target.value);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md outline-none focus:border-orangeBrand"
                />
              </div>

              {/* Filter */}
              <div className="flex justify-center gap-2 mb-3">
                <button
                  onClick={() => setFiltersOpen(true)}
                  className={`relative text-sm font-medium px-2.5 py-1 rounded-md shadow transition flex items-center justify-center ${localFilters.categoryIds.length || localFilters.fontenehusIds.length || localFilters.allergenIds.length || localFilters.ingredientIds.length ? "bg-orange-600 text-white" : "bg-orange-500 text-white hover:bg-orange-600"}`}
                  style={{ width: "auto", minWidth: "50px" }}
                >
                  Filtrer
                  {localFilters.categoryIds.length +
                    localFilters.fontenehusIds.length +
                    localFilters.allergenIds.length +
                    localFilters.ingredientIds.length >
                    0 && (
                    <span className="absolute -top-2 -right-2 w-5 h-5 flex items-center justify-center text-xs font-semibold bg-white text-orange-600 rounded-full border border-orange-600">
                      {localFilters.categoryIds.length +
                        localFilters.fontenehusIds.length +
                        localFilters.allergenIds.length +
                        localFilters.ingredientIds.length}
                    </span>
                  )}
                </button>
              </div>
              <FilterModal
                isOpen={filtersOpen}
                onClose={() => setFiltersOpen(false)}
                categories={recipeCategories}
                fontenehusList={fontenehusList}
                allergens={allergensList}
                ingredients={ingredientsList}
                onApplyFilters={setFilters}
              />

              {/* Scrollable recipes */}
              <div
                className="flex-1 overflow-y-auto px-2"
                style={{ maxHeight: "250px" }}
              >
                {filteredRecipes.length > 0 ? (
                  filteredRecipes.map((recipe) => (
                    <div
                      key={recipe.id}
                      className="px-4 py-2 border-b border-gray-100 hover:bg-gray-50 active:bg-gray-100 cursor-pointer rounded-md"
                    >
                      <div className="font-medium text-gray-800">
                        {recipe.title}
                      </div>
                      {recipe.description && (
                        <div className="text-sm text-gray-600 mt-1">
                          {recipe.description}
                        </div>
                      )}
                      <div className="flex justify-center mt-2 gap-x-2">
                        <button
                          onClick={() => setShowModalRecipe(recipe.id)}
                          className="bg-gray-200 hover:bg-gray-300 text-gray-700 text-sm px-3 py-1 rounded-md"
                        >
                          📖 Se oppskrift
                        </button>
                        <button
                          onClick={() => handleSelectRecipe(recipe.id)}
                          className="bg-gray-200 hover:bg-gray-300 text-gray-700 text-sm px-3 py-1 rounded-md"
                        >
                          📖 Legg til
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="px-4 py-4 text-center text-gray-500">
                    {search
                      ? "Ingen oppskrifter funnet"
                      : "Ingen tilgjengelige oppskrifter"}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-gray-200 flex-shrink-0">
                <button
                  onClick={handleClearSelection}
                  className="w-full py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 font-medium"
                >
                  {selectedId ? "Fjern valg" : "Lukk"}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Recipe Modal Portal */}
      {showModalRecipe &&
        createPortal(
          <div className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-center justify-center p-4">
            <RecipeDetail
              isOpen={showModalRecipe != undefined}
              isModal
              recipeId={showModalRecipe}
              onClose={() => setShowModalRecipe(undefined)}
            />
          </div>,
          document.body,
        )}

      {/* Floating suggestions without date */}
      <section className="bg-white shadow p-5 rounded-xl mt-4">
        <button
          onClick={() => {
            const newState = !showFloatingSuggestions;
            if (newState) loadFloatingSuggestions();
            setShowFloatingSuggestions(newState);
          }}
          className="w-full flex justify-between items-center text-lg font-semibold text-orangeBrand"
        >
          <span>Foreslåtte lunsjer uten dato</span>
          <span className="text-gray-500">
            {showFloatingSuggestions ? "▲" : "▼"}
          </span>
        </button>
        <div
          className={`transition-all duration-300 overflow-hidden ${showFloatingSuggestions ? "max-h-[1000px] mt-4" : "max-h-0"}`}
        >
          {floatingLoading ? (
            <p className="text-center text-gray-600 italic py-2">
              Laster forslag...
            </p>
          ) : floatingSuggestions.length === 0 ? (
            <p className="text-gray-600 italic text-center">
              Ingen udaterte forslag.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {floatingSuggestions.map((item) => (
                <div
                  key={item.id}
                  className="bg-white rounded-lg shadow-sm p-3 flex flex-col items-center"
                >
                  <div
                    onClick={() => setModalRecipeId(item.recipeId)}
                    className="bg-orange-50 hover:bg-orange-100 p-3 rounded-lg shadow-sm text-left flex flex-col items-center justify-center cursor-pointer flex-1"
                  >
                    <p className="font-medium text-orangeBrand text-center">
                      {item.recipeTitle}
                    </p>
                    <p className="text-sm text-gray-600 mt-1 text-center">
                      👤 Foreslått av{" "}
                      <strong>{item.suggestedByUsername ?? "Anonym"}</strong>
                    </p>
                  </div>

                  {/* Like Section */}
                  <div className="flex items-center justify-between w-full mt-2 px-2">
                    <button
                      onClick={() => canLike && handleLike(item.id, true)}
                      disabled={!canLike}
                      className={`flex items-center gap-1 px-3 py-1 rounded-md text-sm transition-colors ${
                        item.isLikedByCurrentUser
                          ? "bg-blue-500 text-white hover:bg-blue-600"
                          : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                      } ${!canLike ? "cursor-not-allowed opacity-60" : ""}`}
                    >
                      <span>👍</span>
                      <span>
                        {canLike
                          ? item.isLikedByCurrentUser
                            ? "Likt"
                            : "Lik"
                          : "Liker"}
                      </span>
                    </button>

                    <div className="text-sm text-gray-600">
                      {item.likeCount! > 0 && (
                        <span>
                          {item.likeCount} liker{item.likeCount !== 1 ? "" : ""}
                        </span>
                      )}
                    </div>
                  </div>

                  {canAddRecipeToDailyMenu && (
                    <button
                      onClick={() =>
                        onAddRecipeFromSuggestion?.(date, item.recipeId)
                      }
                      className="mt-2 bg-green-500 hover:bg-green-600 text-white text-sm px-3 py-1 rounded-md flex items-center gap-1"
                    >
                      Legg til i menyen
                    </button>
                  )}
                  {canRemoveSuggestion(item.suggestedById) && (
                    <button
                      onClick={() => handleRemove(item.id, true)}
                      className="mt-2 bg-red-500 hover:bg-red-600 text-white text-sm px-3 py-1 rounded-md"
                    >
                      ❌ Fjern
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default SuggestedLunches;
